import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, ChevronRight, X, Save, RefreshCw, Star, Slash, AlertTriangle,
  Plus, Upload, Trash2, Lock, RotateCcw, CheckCircle2
} from 'lucide-react';
import { getModel, getModelByUsername, patchModel, patchFlags, replaceTags } from '../api/models';
import { suggestTags } from '../api/tags';
import { batchAddPagePins, getPagePinsForUser } from '../api/pins';
import { getActiveSource, setActiveSource } from '../api/activeSource';
import ModelPhotoGallery from '../components/ModelPhotoGallery';
import TagsTreeSelector, { TagTreeNode } from '../components/TagsTreeSelector';
import { AdminModelView, AdminPatchModelDto, AdminFlagsDto } from '../api/types';
import { SaveBar } from '../components/shared/SaveBar';
import { useConfirm } from '../components/shared/ConfirmDialog';
import tagsTreeData from '../../tags_tree.json';

interface ModelEditPageProps {
    modelId?: number;
    onClose?: () => void;
}

const ModelEditPage: React.FC<ModelEditPageProps> = ({ modelId: propModelId, onClose: propOnClose }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const modelId = propModelId ?? Number(id);

    const [notifications, setNotifications] = useState<{ id: string; type: string; message: string }[]>([]);
    const skipNextFormReset = useRef(false);

    const [form, setForm] = useState({
        modelName: '', description: '', onlyfansUrl: '', price: '', likesCount: 0,
        manualImage: false, manualDescription: false, manualPrice: false,
    });
    const [flagsForm, setFlagsForm] = useState<AdminFlagsDto>({});
    const formRef = useRef(form);
    formRef.current = form;

    const [allTags, setAllTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [tagSuggestions, setTagSuggestions] = useState<any[]>([]);
    const [showTagSuggestions, setShowTagSuggestions] = useState(false);
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const tagFileInputRef = useRef<HTMLInputElement>(null);

    const [selectedPages, setSelectedPages] = useState<string[][]>([]);
    const [pagePinsLoading, setPagePinsLoading] = useState(false);
    const [pagePinsSaving, setPagePinsSaving] = useState(false);
    const [bulkImportProcessing, setBulkImportProcessing] = useState(false);
    const [showPagePins, setShowPagePins] = useState(false);
    const tagsTree: TagTreeNode[] = (tagsTreeData as any).tagsTree || [];

    const { data: model, isLoading, error, refetch } = useQuery({
        queryKey: ['model', modelId],
        queryFn: () => getModel(modelId),
        enabled: Number.isFinite(modelId)
    });

    useEffect(() => {
        if (!model) return;
        if (skipNextFormReset.current) {
            setForm(prev => ({
                ...prev,
                manualImage: model.manualImage || false,
                manualDescription: model.manualDescription || false,
                manualPrice: model.manualPrice || false,
            }));
            skipNextFormReset.current = false;
        } else {
            setForm({
                modelName: model.modelName || '', description: model.description || '',
                onlyfansUrl: model.onlyfansUrl || '', price: model.price || '',
                likesCount: model.likesCount || 0,
                manualImage: model.manualImage || false, manualDescription: model.manualDescription || false,
                manualPrice: model.manualPrice || false,
            });
        }
        setFlagsForm({
            promote: model.promote || false, blacklisted: model.blacklisted || false,
            creatorPage: model.creatorPage || false, dmcaBlocked: model.dmcaBlocked || false,
        });
        setAllTags(model.tags || []);
    }, [model]);

    useEffect(() => {
        if (showPagePins && model?.modelUsername) {
            setPagePinsLoading(true);
            getPagePinsForUser(model.modelUsername)
                .then(data => setSelectedPages((data.pages || []).map((p: any) => Array.isArray(p) ? p : (p.tags || []))))
                .catch(() => notify('error', 'Failed to load page pins'))
                .finally(() => setPagePinsLoading(false));
        }
    }, [showPagePins, model?.modelUsername]);

    const notify = useCallback((type: string, message: string) => {
        const nid = Date.now().toString();
        setNotifications(prev => [...prev, { id: nid, type, message }]);
        setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== nid)), 3500);
    }, []);

    const saveMutation = useMutation({
        mutationFn: (data: AdminPatchModelDto) => patchModel(modelId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['model', modelId] });
            queryClient.invalidateQueries({ queryKey: ['models'] });
            skipNextFormReset.current = true;
            refetch();
        },
        onError: () => notify('error', 'Failed to save')
    });

    const flagsMutation = useMutation({
        mutationFn: (data: AdminFlagsDto) => patchFlags(modelId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['model', modelId] });
            queryClient.invalidateQueries({ queryKey: ['models'] });
            refetch();
        },
        onError: () => notify('error', 'Failed to update status')
    });

    const tagsMutation = useMutation({
        mutationFn: (tags: string[]) => replaceTags(modelId, { tags }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['model', modelId] });
            queryClient.invalidateQueries({ queryKey: ['models'] });
            skipNextFormReset.current = true;
            refetch();
        },
        onError: () => notify('error', 'Failed to update tags')
    });

    // Phase 2: geo OF/OT active-source toggle. Only meaningful when this username has BOTH an OF
    // and an OT row (Path A) — check for the sibling row before showing anything. A 404 here is
    // the expected/common case (no sibling), not a transient error worth retrying.
    const username = model?.modelUsername;
    const ownSource = model?.source === 'OF' || model?.source === 'OT' ? model.source : null;
    const siblingSource = ownSource === 'OF' ? 'OT' : ownSource === 'OT' ? 'OF' : null;

    const { data: siblingModel } = useQuery({
        queryKey: ['model-sibling-source', username, siblingSource],
        queryFn: () => getModelByUsername(username!, siblingSource!),
        enabled: !!username && !!siblingSource,
        retry: false,
    });

    const hasBothSources = !!ownSource && !!siblingModel;

    const { data: activeSource } = useQuery({
        queryKey: ['active-source', username],
        queryFn: () => getActiveSource(username!),
        enabled: hasBothSources,
        retry: false,
    });

    const activeSourceMutation = useMutation({
        mutationFn: (source: 'OF' | 'OT') => setActiveSource(username!, source),
        onSuccess: (view) => {
            queryClient.setQueryData(['active-source', username], view);
            notify('info', `Active listing order: ${view.source}`);
        },
        onError: () => notify('error', 'Failed to set active source')
    });

    const saveField = useCallback((field: keyof AdminPatchModelDto, value: any, autoManual?: string) => {
        const patch: AdminPatchModelDto = { [field]: value };
        if (autoManual) (patch as any)[autoManual] = true;
        saveMutation.mutate(patch);
    }, [saveMutation]);

    const handleFieldChange = (field: string, value: any) => setForm(prev => ({ ...prev, [field]: value }));

    const toggleFlag = (field: keyof AdminFlagsDto) => {
        const newFlags = { ...flagsForm, [field]: !flagsForm[field] };
        setFlagsForm(newFlags);
        flagsMutation.mutate(newFlags);
    };

    const toggleManualFlag = (field: 'manualImage' | 'manualDescription' | 'manualPrice', label: string) => {
        const newVal = !form[field];
        setForm(prev => ({ ...prev, [field]: newVal }));
        patchModel(modelId, { [field]: newVal }).then(() => {
            notify('info', `${label}: ${newVal ? 'manual override' : 'auto-sync'}`);
            queryClient.setQueryData(['model', modelId], (old: any) => old ? { ...old, [field]: newVal } : old);
        }).catch(() => {
            setForm(prev => ({ ...prev, [field]: !newVal }));
            notify('error', `Failed to update ${label}`);
        });
    };

    const handleTagInput = async (value: string) => {
        setTagInput(value);
        if (value.length >= 2) {
            try { const s = await suggestTags(value); setTagSuggestions(s); setShowTagSuggestions(true); }
            catch { setShowTagSuggestions(false); }
        } else setShowTagSuggestions(false);
    };

    const addTag = (tag: string) => {
        if (tag && !allTags.includes(tag)) { const nt = [...allTags, tag]; setAllTags(nt); tagsMutation.mutate(nt); }
        setTagInput(''); setShowTagSuggestions(false);
    };

    const removeTag = (tag: string) => { const nt = allTags.filter(t => t !== tag); setAllTags(nt); tagsMutation.mutate(nt); };

    const clearAllTags = () => { if (!window.confirm(`Remove all ${allTags.length} tags?`)) return; setAllTags([]); tagsMutation.mutate([]); };

    const handleTagFileUpload = (file: File) => {
        if (!window.confirm('This will REPLACE all current tags. Continue?')) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const newTags = [...new Set((e.target?.result as string).split('\n').map(l => l.trim().toLowerCase()).filter(l => l.length > 0 && !l.startsWith('#')))];
            if (newTags.length > 0) { setAllTags(newTags); tagsMutation.mutate(newTags); notify('success', `${newTags.length} tags loaded`); }
            else notify('error', 'File is empty');
        };
        reader.readAsText(file);
    };

    const handleFileDrop = (e: React.DragEvent) => {
        e.preventDefault(); setIsDraggingFile(false);
        const f = e.dataTransfer.files[0];
        if (f && (f.name.endsWith('.txt') || f.type === 'text/plain')) handleTagFileUpload(f);
        else notify('error', 'Only .txt files are accepted');
    };

    // Page pins helpers
    const extractAllKnownTags = (nodes: TagTreeNode[]): Set<string> => {
        const tags = new Set<string>();
        const traverse = (n: TagTreeNode) => { tags.add(n.tag); n.children?.forEach(traverse); };
        nodes.forEach(traverse); return tags;
    };
    const parseUrlToTags = (url: string): string[] | null => {
        try {
            for (const part of new URL(url.trim()).pathname.split('/').filter(Boolean)) {
                if (!part.startsWith('onlyfans-')) continue;
                const known = extractAllKnownTags(tagsTree);
                const result: string[] = []; let rem = part.substring(9);
                while (rem.length > 0) {
                    let matched = false;
                    for (let p = Math.min(5, rem.split('-').length); p > 0; p--) {
                        const c = rem.split('-').slice(0, p).join('-');
                        if (known.has(c)) { result.push(c); rem = rem.substring(c.length); if (rem.startsWith('-')) rem = rem.substring(1); matched = true; break; }
                    }
                    if (!matched) { const p = rem.split('-'); result.push(p[0]); rem = p.slice(1).join('-'); }
                }
                return result.length > 0 ? result : null;
            }
            return null;
        } catch { return null; }
    };
    const handleBulkImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]; if (!file) return;
        setBulkImportProcessing(true);
        try {
            const lines = (await file.text()).split('\n').map(l => l.trim()).filter(Boolean);
            const extracted = lines.map(parseUrlToTags).filter(Boolean) as string[][];
            if (extracted.length > 0) {
                const existing = new Set(selectedPages.map(p => JSON.stringify(p)));
                const newP = extracted.filter(p => !existing.has(JSON.stringify(p)));
                setSelectedPages([...selectedPages, ...newP]);
                notify('success', `${newP.length} pages imported`);
            } else notify('error', 'No valid URLs found');
        } catch { notify('error', 'Failed to read file'); }
        finally { setBulkImportProcessing(false); event.target.value = ''; }
    };
    const handlePagePinsSave = async () => {
        if (!model?.modelUsername) return;
        setPagePinsSaving(true);
        try { await batchAddPagePins({ username: model.modelUsername, pages: selectedPages }); notify('success', `${selectedPages.length} page pins saved`); }
        catch { notify('error', 'Failed to save page pins'); }
        finally { setPagePinsSaving(false); }
    };

    const fmt = (v: number | null | undefined) => typeof v === 'number' && !Number.isNaN(v) ? new Intl.NumberFormat('en-US').format(v) : '—';

    const handleClose = useCallback(() => {
        if (propOnClose) propOnClose();
        else navigate('/models');
    }, [propOnClose, navigate]);

    // Compute dirty state at top level — must run unconditionally for hooks rules.
    // When model isn't loaded yet, isDirty is false.
    const isDirty = !!model && (
        form.modelName !== (model.modelName || '') ||
        form.description !== (model.description || '') ||
        form.onlyfansUrl !== (model.onlyfansUrl || '') ||
        form.price !== (model.price || '') ||
        form.likesCount !== (model.likesCount || 0)
    );

    // beforeunload guard while dirty — must be at top level, before any conditional returns.
    useEffect(() => {
        if (!isDirty) return;
        const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // ─── Loading / Error ───
    const isModal = Boolean(propOnClose);

    if (!Number.isFinite(modelId) || error || (!isLoading && !model)) {
        const inner = (
            <div className="hf-card" style={{ padding: 40, textAlign: 'center' }}>
                <AlertTriangle size={32} style={{ color: 'var(--danger)', marginBottom: 8 }} />
                <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>
                    {error ? 'Failed to load model' : 'Model not found'}
                </h2>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button className="hf-btn primary" onClick={() => refetch()}><RotateCcw size={14} /> Retry</button>
                    <button className="hf-btn" onClick={handleClose}>{isModal ? 'Close' : 'Back'}</button>
                </div>
            </div>
        );
        return isModal ? <div className="hf-overlay" onClick={handleClose}>{inner}</div> : <>{inner}</>;
    }
    if (isLoading || !model) {
        const inner = (
            <div className="hf-card" style={{ padding: 40, textAlign: 'center', color: 'var(--fg-3)' }}>
                Loading model…
            </div>
        );
        return isModal ? <div className="hf-overlay" onClick={handleClose}>{inner}</div> : <>{inner}</>;
    }

    const avatarUrl = model.imageUrls ? Object.values(model.imageUrls)[0] : null;
    const initial = (model.modelName || model.modelUsername || '?').trim().charAt(0).toUpperCase();

    const closeWithGuard = async () => {
        if (isDirty) {
            const ok = await confirm({
                title: 'Discard unsaved changes?',
                description: `You have unsaved edits on @${model.modelUsername}. Closing now will lose them.`,
                confirmLabel: 'Discard',
                cancelLabel: 'Keep editing',
                tone: 'warning'
            });
            if (!ok) return;
        }
        handleClose();
    };

    const SECTIONS = [
        { id: 'identity',     label: 'Identity'     },
        { id: 'monetisation', label: 'Monetisation' },
        { id: 'imagery',      label: 'Imagery'      },
        { id: 'tags',         label: 'Tags'         },
        { id: 'page-pins',    label: 'Page pins'    },
        { id: 'danger',       label: 'Danger zone'  }
    ];

    const saveAllFields = () => {
        const patch: AdminPatchModelDto = {
            modelName: form.modelName,
            description: form.description,
            onlyfansUrl: form.onlyfansUrl,
            price: form.price,
            likesCount: form.likesCount
        };
        saveMutation.mutate(patch);
    };

    const body = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Notifications */}
            {notifications.length > 0 && (
                <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {notifications.map(n => (
                        <div
                            key={n.id}
                            className={`hf-banner ${n.type === 'success' ? 'success' : n.type === 'error' ? 'danger' : ''}`}
                            style={{ minWidth: 280, boxShadow: 'var(--shadow-lg)' }}
                        >
                            {n.type === 'success' && <CheckCircle2 size={14} style={{ color: 'var(--success)' }} />}
                            {n.type === 'error' && <AlertTriangle size={14} style={{ color: 'var(--danger)' }} />}
                            <span>{n.message}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Sticky breadcrumb + close */}
            <div className="hf-sticky-bar" style={{ padding: '12px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--fg-3)', minWidth: 0 }}>
                        <Link to="/models" style={{ color: 'inherit', textDecoration: 'none' }}>Models</Link>
                        <ChevronRight size={12} />
                        <span className="hf-mono" style={{ color: 'var(--fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '40vw' }}>
                            @{model.modelUsername}
                        </span>
                        <ChevronRight size={12} />
                        <span style={{ color: 'var(--fg-1)', fontWeight: 500 }}>Edit</span>
                    </span>
                    {isDirty && (
                        <span className="hf-tag warning sm" aria-live="polite">
                            unsaved changes
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button type="button" className="hf-btn ghost" onClick={closeWithGuard}>
                        {isModal ? <><X size={14} /> Close</> : <><ArrowLeft size={14} /> Back</>}
                    </button>
                </div>
            </div>

            {/* Header: avatar + title + status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span className="hf-avatar lg" style={{ overflow: 'hidden' }}>
                    {avatarUrl
                        ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                        : initial}
                </span>
                <div style={{ minWidth: 0 }}>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {model.modelName || model.modelUsername}
                        <span className="hf-mono" style={{ fontSize: 14, color: 'var(--fg-3)', fontWeight: 400 }}>#{model.id}</span>
                    </h1>
                    <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--fg-2)' }}>
                        <span className="hf-mono">@{model.modelUsername}</span>
                        {' · '}{model.source ?? '—'}{' · '}{model.monetizationGroup ?? '—'}
                    </p>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {model.promote && <span className="hf-tag accent">promote</span>}
                    {model.blacklisted && <span className="hf-tag danger">blacklisted</span>}
                    {model.dmcaBlocked && <span className="hf-tag warning">DMCA</span>}
                </div>
            </div>

            {/* Two-column: form sections + scroll-spy outline */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 16, alignItems: 'start' }}>
                {/* Form sections */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>

                    {/* 1 — Identity */}
                    <section id="identity" className="hf-card">
                        <div className="hf-card-header"><h3 className="hf-card-title">Identity</h3></div>
                        <div className="hf-card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <FormRow label="Username">
                                <div className="hf-input">
                                    <span className="hf-mono" style={{ color: 'var(--fg-3)' }}>@</span>
                                    <input value={model.modelUsername} disabled />
                                </div>
                            </FormRow>
                            <FormRow label="Display name">
                                <div className="hf-input">
                                    <input
                                        value={form.modelName}
                                        onChange={(e) => handleFieldChange('modelName', e.target.value)}
                                        onBlur={() => saveField('modelName', formRef.current.modelName)}
                                        placeholder="Display name"
                                    />
                                </div>
                            </FormRow>
                            <FormRow label="Source">
                                <div className="hf-input">
                                    <input value={model.source ?? '—'} disabled className="hf-mono" />
                                </div>
                            </FormRow>
                            {hasBothSources && (
                                <FormRow label="Active listing order (geo: open)" wide>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <FlagToggle
                                            on={(activeSource?.source ?? 'OF') === 'OF'}
                                            onClick={() => activeSourceMutation.mutate('OF')}
                                            icon={<Star size={12} />}
                                            label="OF active"
                                            variant="accent"
                                        />
                                        <FlagToggle
                                            on={(activeSource?.source ?? 'OF') === 'OT'}
                                            onClick={() => activeSourceMutation.mutate('OT')}
                                            icon={<Star size={12} />}
                                            label="OT active"
                                            variant="accent"
                                        />
                                        <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                                            {activeSource?.source ? 'Manual override' : 'Default (OF)'} — wins the open-geo listing; blocked geo always shows OT.
                                        </span>
                                    </div>
                                </FormRow>
                            )}
                            <FormRow label="Likes">
                                <div className="hf-input">
                                    <input
                                        type="number"
                                        min={0}
                                        value={form.likesCount || ''}
                                        onChange={(e) => handleFieldChange('likesCount', parseInt(e.target.value, 10) || 0)}
                                        onBlur={() => saveField('likesCount', formRef.current.likesCount)}
                                        placeholder="0"
                                    />
                                </div>
                            </FormRow>
                            <FormRow label="OnlyFans URL" wide>
                                <div className="hf-input">
                                    <input
                                        type="url"
                                        value={form.onlyfansUrl}
                                        onChange={(e) => handleFieldChange('onlyfansUrl', e.target.value)}
                                        onBlur={() => saveField('onlyfansUrl', formRef.current.onlyfansUrl)}
                                        placeholder="https://onlyfans.com/…"
                                    />
                                </div>
                            </FormRow>
                            <FormRow label={
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    Bio
                                    <button
                                        type="button"
                                        onClick={() => toggleManualFlag('manualDescription', 'Description')}
                                        title={form.manualDescription ? 'Locked (manual)' : 'Auto-sync'}
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: form.manualDescription ? 'var(--accent)' : 'var(--fg-3)', display: 'inline-flex', alignItems: 'center', padding: 0 }}
                                    >
                                        {form.manualDescription ? <Lock size={11} /> : <RefreshCw size={11} />}
                                    </button>
                                </span>
                            } wide>
                                <textarea
                                    className="hf-textarea"
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    onBlur={() => saveField('description', formRef.current.description, 'manualDescription')}
                                    placeholder="Short bio"
                                />
                            </FormRow>
                        </div>
                    </section>

                    {/* 2 — Monetisation */}
                    <section id="monetisation" className="hf-card">
                        <div className="hf-card-header">
                            <h3 className="hf-card-title">
                                Monetisation
                                {model.promote && <span className="hf-tag accent sm" style={{ marginLeft: 8 }}>
                                    <Star size={10} /> promoted
                                </span>}
                            </h3>
                        </div>
                        <div className="hf-card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                            <FormRow label="Type">
                                <div className="hf-input">
                                    <input className="hf-mono" value={model.monetizationGroup ?? '—'} disabled />
                                </div>
                                <span className="hf-help">PPC / CPL / REVSHARE</span>
                            </FormRow>
                            <FormRow label="PPC type">
                                <div className="hf-input">
                                    <input className="hf-mono" value={model.ppcType ?? '—'} disabled />
                                </div>
                            </FormRow>
                            <FormRow label="PPC order">
                                <div className="hf-input">
                                    <input className="hf-mono" type="number" value={model.ppcOrder ?? ''} disabled placeholder="—" />
                                </div>
                            </FormRow>
                            <FormRow label={
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    Price (CPC)
                                    <button
                                        type="button"
                                        onClick={() => toggleManualFlag('manualPrice', 'Price')}
                                        title={form.manualPrice ? 'Locked (manual)' : 'Auto-sync'}
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: form.manualPrice ? 'var(--accent)' : 'var(--fg-3)', display: 'inline-flex', alignItems: 'center', padding: 0 }}
                                    >
                                        {form.manualPrice ? <Lock size={11} /> : <RefreshCw size={11} />}
                                    </button>
                                </span>
                            }>
                                <div className="hf-input">
                                    <span className="hf-mono" style={{ color: 'var(--fg-3)' }}>$</span>
                                    <input
                                        value={form.price}
                                        onChange={(e) => handleFieldChange('price', e.target.value)}
                                        onBlur={() => saveField('price', formRef.current.price, 'manualPrice')}
                                        placeholder="0.045"
                                    />
                                </div>
                            </FormRow>
                            <FormRow label="Priority">
                                <div className="hf-input">
                                    <input className="hf-mono" type="number" value={model.priority ?? ''} disabled placeholder="0" />
                                </div>
                            </FormRow>
                            <FormRow label="Manual bias">
                                <div className="hf-input">
                                    <input className="hf-mono" type="number" value={model.manualBias ?? ''} disabled placeholder="0" />
                                </div>
                            </FormRow>

                            {/* Flag toggles */}
                            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                <FlagToggle
                                    on={!!flagsForm.promote}
                                    onClick={() => toggleFlag('promote')}
                                    icon={<Star size={12} />}
                                    label="Show in promote"
                                    variant="accent"
                                />
                                <FlagToggle
                                    on={!!flagsForm.creatorPage}
                                    onClick={() => toggleFlag('creatorPage')}
                                    icon={<CheckCircle2 size={12} />}
                                    label="Creator page"
                                    variant="success"
                                />
                            </div>
                        </div>
                    </section>

                    {/* 3 — Imagery */}
                    <section id="imagery" className="hf-card">
                        <div className="hf-card-header">
                            <h3 className="hf-card-title">
                                Imagery
                                <button
                                    type="button"
                                    onClick={() => toggleManualFlag('manualImage', 'Image')}
                                    title={form.manualImage ? 'Locked (manual)' : 'Auto-sync'}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: form.manualImage ? 'var(--accent)' : 'var(--fg-3)', display: 'inline-flex', alignItems: 'center', padding: 0, marginLeft: 8 }}
                                >
                                    {form.manualImage ? <Lock size={12} /> : <RefreshCw size={12} />}
                                </button>
                            </h3>
                        </div>
                        <div className="hf-card-body">
                            <ModelPhotoGallery
                                model={model}
                                onChanged={() => {
                                    setForm(prev => ({ ...prev, manualImage: true }));
                                    refetch();
                                }}
                                onNotify={notify}
                            />
                        </div>
                    </section>

                    {/* 4 — Tags */}
                    <section id="tags" className="hf-card">
                        <div className="hf-card-header">
                            <h3 className="hf-card-title">Tags</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span className="hf-count">{allTags.length}</span>
                                {allTags.length > 0 && (
                                    <button className="hf-btn ghost sm" onClick={clearAllTags}>
                                        <Trash2 size={12} /> Clear all
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ position: 'relative' }}>
                                <div className="hf-input">
                                    <Plus size={13} style={{ color: 'var(--fg-3)' }} />
                                    <input
                                        value={tagInput}
                                        onChange={(e) => handleTagInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); if (tagInput.trim()) addTag(tagInput.trim()); }
                                        }}
                                        placeholder="add tag…"
                                    />
                                </div>
                                {showTagSuggestions && tagSuggestions.length > 0 && (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-2)', boxShadow: 'var(--shadow-md)', maxHeight: 220, overflow: 'auto', zIndex: 10 }}>
                                        {tagSuggestions.map((s: { slug: string; name: string; modelCount: number }, i: number) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => addTag(s.slug)}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    padding: '8px 12px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    borderBottom: '1px solid var(--border)',
                                                    cursor: 'pointer',
                                                    fontFamily: 'inherit',
                                                    color: 'var(--fg-1)',
                                                    fontSize: 12.5
                                                }}
                                            >
                                                <strong>{s.name}</strong>
                                                <div className="hf-help" style={{ marginTop: 0 }}>
                                                    <span className="hf-mono">{s.slug}</span> · {s.modelCount}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div
                                className="hf-feature-box muted"
                                style={{
                                    padding: 12,
                                    border: `1px dashed var(--${isDraggingFile ? 'accent' : 'border-2'})`,
                                    background: isDraggingFile ? 'var(--accent-soft)' : 'var(--surface-2)',
                                    textAlign: 'center',
                                    cursor: 'pointer'
                                }}
                                onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                                onDragLeave={() => setIsDraggingFile(false)}
                                onDrop={handleFileDrop}
                                onClick={() => tagFileInputRef.current?.click()}
                            >
                                <input
                                    ref={tagFileInputRef}
                                    type="file"
                                    accept=".txt,text/plain"
                                    style={{ display: 'none' }}
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) { handleTagFileUpload(f); e.target.value = ''; } }}
                                />
                                <Upload size={16} style={{ color: 'var(--fg-3)' }} />
                                <span className="hf-help" style={{ marginTop: 4, display: 'block' }}>
                                    Drop .txt to replace ALL tags
                                </span>
                            </div>

                            {allTags.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {allTags.map((tag) => (
                                        <span key={tag} className={`hf-tag ${tag === 'trans' ? 'warning' : ''}`}>
                                            {tag}
                                            <button
                                                onClick={() => removeTag(tag)}
                                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, marginLeft: 2, display: 'inline-flex' }}
                                                aria-label={`Remove ${tag}`}
                                            >
                                                <X size={10} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* 6 — Page pins */}
                    <section id="page-pins" className="hf-card">
                        <div className="hf-card-header">
                            <h3 className="hf-card-title">Page pins</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span className="hf-count">{selectedPages.length}</span>
                                <button
                                    className="hf-btn ghost sm"
                                    onClick={() => setShowPagePins(!showPagePins)}
                                >
                                    {showPagePins ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </div>
                        {showPagePins && (
                            <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {pagePinsLoading ? (
                                    <div style={{ textAlign: 'center', color: 'var(--fg-3)' }}>Loading…</div>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <input
                                                type="file"
                                                id="pp-bulk"
                                                accept=".txt"
                                                onChange={handleBulkImport}
                                                disabled={bulkImportProcessing}
                                                style={{ display: 'none' }}
                                            />
                                            <button
                                                className="hf-btn sm"
                                                onClick={() => document.getElementById('pp-bulk')?.click()}
                                                disabled={bulkImportProcessing}
                                            >
                                                <Upload size={12} /> {bulkImportProcessing ? '…' : 'Import .txt'}
                                            </button>
                                            <span className="hf-count" style={{ marginLeft: 'auto' }}>{selectedPages.length} pages</span>
                                        </div>
                                        <TagsTreeSelector
                                            tagsTree={tagsTree}
                                            selectedPages={selectedPages}
                                            onChange={setSelectedPages}
                                        />
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                            {selectedPages.length > 0 && (
                                                <button
                                                    className="hf-btn ghost sm"
                                                    onClick={() => {
                                                        if (window.confirm(`Remove ${selectedPages.length} pins?`)) {
                                                            setSelectedPages([]);
                                                        }
                                                    }}
                                                >
                                                    <Trash2 size={12} /> Clear
                                                </button>
                                            )}
                                            <button
                                                className="hf-btn primary sm"
                                                onClick={handlePagePinsSave}
                                                disabled={pagePinsSaving}
                                            >
                                                <Save size={12} /> Save pins
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </section>

                    {/* 7 — Danger zone */}
                    <section id="danger" className="hf-card danger-zone">
                        <div className="hf-card-header" style={{ borderBottom: '1px solid var(--danger-soft)' }}>
                            <h3 className="hf-card-title" style={{ color: 'var(--danger)' }}>
                                <AlertTriangle size={13} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                                Danger zone
                            </h3>
                        </div>
                        <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <div>
                                    <strong style={{ fontSize: 13, color: 'var(--fg-1)' }}>Blacklist</strong>
                                    <div className="hf-help" style={{ marginTop: 2 }}>Hide this model from all public listings.</div>
                                </div>
                                <button
                                    className={`hf-btn ${flagsForm.blacklisted ? 'danger' : ''}`}
                                    onClick={() => toggleFlag('blacklisted')}
                                >
                                    <Slash size={14} />
                                    {flagsForm.blacklisted ? 'Unblacklist' : 'Blacklist'}
                                </button>
                            </div>
                            <div className="hf-divider" />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <div>
                                    <strong style={{ fontSize: 13, color: 'var(--fg-1)' }}>DMCA block</strong>
                                    <div className="hf-help" style={{ marginTop: 2 }}>Remove from listings due to copyright claim.</div>
                                </div>
                                <button
                                    className={`hf-btn ${flagsForm.dmcaBlocked ? 'danger' : ''}`}
                                    onClick={() => toggleFlag('dmcaBlocked')}
                                >
                                    {flagsForm.dmcaBlocked ? 'Unblock' : 'DMCA block'}
                                </button>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Right rail: scroll-spy outline */}
                <aside style={{ position: 'sticky', top: 80 }}>
                    <div className="hf-card">
                        <div className="hf-card-header"><h3 className="hf-card-title">Sections</h3></div>
                        <div className="hf-card-body" style={{ padding: 6 }}>
                            {SECTIONS.map(s => (
                                <a
                                    key={s.id}
                                    href={`#${s.id}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }}
                                    style={{
                                        display: 'block',
                                        padding: '6px 10px',
                                        fontSize: 12.5,
                                        color: 'var(--fg-2)',
                                        borderRadius: 'var(--r-1)',
                                        textDecoration: 'none'
                                    }}
                                    className="hf-outline-link"
                                >
                                    {s.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>

            {/* Floating Save bar — always visible while dirty */}
            <SaveBar
                dirty={isDirty}
                saving={saveMutation.isPending}
                onSave={saveAllFields}
                onCancel={async () => {
                    if (!isDirty) return;
                    const ok = await confirm({
                        title: 'Discard changes?',
                        description: <>Revert all edits to <span className="hf-mono">@{model.modelUsername}</span>?</>,
                        confirmLabel: 'Discard',
                        tone: 'warning'
                    });
                    if (!ok) return;
                    setForm({
                        modelName: model.modelName || '',
                        description: model.description || '',
                        onlyfansUrl: model.onlyfansUrl || '',
                        price: model.price || '',
                        likesCount: model.likesCount || 0,
                        manualImage: form.manualImage,
                        manualDescription: form.manualDescription,
                        manualPrice: form.manualPrice,
                    });
                }}
                hint={
                    saveMutation.isPending
                        ? 'Sending…'
                        : isDirty
                            ? <>Edits to <span className="hf-mono">@{model.modelUsername}</span></>
                            : null
                }
            />
        </div>
    );

    if (isModal) {
        return (
            <div className="hf-overlay" onClick={closeWithGuard}>
                <div
                    style={{ background: 'var(--bg)', borderRadius: 'var(--r-4)', maxWidth: 1100, width: '100%', maxHeight: '92vh', overflow: 'auto', padding: '20px 24px' }}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                >
                    {body}
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px 28px' }}>
            {body}
        </div>
    );

};

const FormRow: React.FC<{ label: React.ReactNode; children: React.ReactNode; wide?: boolean }> = ({ label, children, wide }) => (
    <div style={{ gridColumn: wide ? '1 / -1' : undefined }}>
        <div className="hf-label-row"><span className="hf-label">{label}</span></div>
        {children}
    </div>
);

const FlagToggle: React.FC<{ on: boolean; onClick: () => void; icon: React.ReactNode; label: string; variant?: 'accent' | 'success' | 'danger' | 'warning' }> = ({ on, onClick, icon, label, variant }) => (
    <button
        type="button"
        onClick={onClick}
        className={`hf-btn sm ${on ? (variant === 'danger' ? 'danger' : variant === 'warning' ? '' : variant === 'success' ? 'success' : 'primary') : ''}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
        {icon} {label}
    </button>
);

export default ModelEditPage;
